import React, { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { FieldValues, useController } from "react-hook-form";
import styled from "@emotion/styled";
import { Input, Popover } from "antd5";
import { Flex } from "styles/utility-components";

import {
  SupplierOption,
  SupplierSelectCategory,
} from "components/form_components/dropdown_list_selects/new_supplier_selects/types";
import { SelectProps } from "components/form_components/Inputs";
import {
  checkIfAllSignalsSelected,
  convertOrgToSupplierOption,
} from "components/form_components/SupplierSelect";
import Button from "components/ui/button/Button";
import { useDebouncedValue } from "lib/debounce";
import { Organisation } from "lib/generated/app-service-gql/graphql";
import { useSearchOrganisations } from "lib/hooks/api/organisations/useSearchOrganisations";
import { useSignalSettingsGQL } from "lib/hooks/api/teams/useSignalSettingsGQL";
import UIcon, { IconKey } from "lib/icons/untitled_ui/UIcon";
import { OrgSortBy, OrgSortOrder, SearchOrgPrimaryRole } from "lib/types/graphQLEnums";
import { ALL_COMPETITORS_TOKEN, ALL_PARTNERS_TOKEN } from "lib/types/models";
import { deduplicateByKey, isDefined, simpleArrayDedupe } from "lib/utils";
import SimpleSupplierDropdown from "./dropdown_list_selects/new_supplier_selects/SimpleSupplierDropdown";

type FieldProps<T extends FieldValues> = Omit<SelectProps<T>, "options">;

function SupplierSelectPopover<T extends FieldValues>(
  props: FieldProps<T> & {
    title: ReactNode;
    icon: IconKey;
    categoryOptions?: SupplierSelectCategory[];
    incumbentSuppliers?: string[];
  },
) {
  const { title, icon, categoryOptions, incumbentSuppliers } = props;

  const [searchText, setSearchText] = useState<string>("");
  const [debouncedText] = useDebouncedValue(searchText, 300);

  const { field } = useController(props);

  const [selectedSuppliers, setSelectedSuppliers] = useState<SupplierOption[]>([]);

  const { data: signals, isLoading: isLoadingSignals } = useSignalSettingsGQL({
    includeAliases: true,
    includeNotices: true,
  });

  const competitors = useMemo(
    (): SupplierOption[] => convertOrgToSupplierOption(signals?.competitors ?? []),
    [signals?.competitors],
  );

  const partners = useMemo(
    (): SupplierOption[] => convertOrgToSupplierOption(signals?.partners ?? []),
    [signals?.partners],
  );

  const selectedSupplierIds = useMemo((): string[] => {
    if (!field.value) {
      return [];
    }

    let filterValue: string[] = field.value.filter(isDefined);

    if (filterValue.includes(ALL_COMPETITORS_TOKEN)) {
      filterValue = filterValue.concat(competitors.map((s) => s.value));
    }

    if (filterValue.includes(ALL_PARTNERS_TOKEN)) {
      filterValue = filterValue.concat(partners.map((s) => s.value));
    }

    return simpleArrayDedupe(filterValue).filter(
      (val) => ![ALL_COMPETITORS_TOKEN, ALL_PARTNERS_TOKEN].includes(val),
    );
  }, [competitors, field.value, partners]);

  const { data: initialOrganisations } = useSearchOrganisations(
    {
      textSearch: "",
      sortBy: OrgSortBy.Relevance,
      sortOrder: OrgSortOrder.Desc,
      primaryRole: SearchOrgPrimaryRole.Supplier,
      ids: selectedSupplierIds,
      limit: Math.max(selectedSupplierIds.length, 1),
      page: 1,
    },
    false,
    false,
    {
      enabled: selectedSupplierIds.length > 0 && selectedSuppliers.length === 0,
    },
  );

  useEffect(() => {
    if (initialOrganisations) {
      const initialOrgs = initialOrganisations.searchOrganisations.orgs;
      setSelectedSuppliers(
        initialOrgs.map((o) => ({
          label: o.name,
          value: o.id,
          tagLabel: incumbentSuppliers?.includes(o.id) ? "Incumbent" : undefined,
        })),
      );
    }
  }, [initialOrganisations, incumbentSuppliers]);

  const handleSuppliersSelection = useCallback(
    (newSuppliers: SupplierOption[]) => {
      const newSuppliersDeuped = Array.from(deduplicateByKey(newSuppliers, (s) => s.value));

      setSelectedSuppliers(newSuppliersDeuped);

      const newSupplierIds = newSuppliersDeuped.map((s) => s.value);

      const newSelectionValues = checkIfAllSignalsSelected(newSupplierIds, competitors, partners);
      field.onChange(newSelectionValues);
    },
    [competitors, field, partners],
  );

  const partnersWithTag = addTagLabel(signals?.partners ?? [], incumbentSuppliers ?? []);
  const competitorsWithTag = addTagLabel(signals?.competitors ?? [], incumbentSuppliers ?? []);

  return (
    <Popover
      trigger={["click"]}
      content={
        <Container>
          <Input
            placeholder="Search suppliers"
            onChange={(e) => setSearchText(e.currentTarget.value.trim())}
            prefix={<UIcon icon="searchSm" color="sysPrimaryDefault" size={16} />}
          />
          <SimpleSupplierDropdown
            handleSuppliersSelection={handleSuppliersSelection}
            textSearch={debouncedText}
            selectedSuppliers={selectedSuppliers}
            competitors={competitorsWithTag ?? []}
            partners={partnersWithTag ?? []}
            categoryOptions={categoryOptions}
            isLoading={isLoadingSignals}
          />
        </Container>
      }
      arrow={false}
      placement="bottomRight"
      overlayInnerStyle={{ padding: 0, width: "300px" }}
    >
      <Button buttonType="secondary" icon={icon}>
        <Flex gap={8} alignItems="center">
          {title}
          <UIcon icon="chevronDown" size={16} />
        </Flex>
      </Button>
    </Popover>
  );
}

function addTagLabel(items: Organisation[], incumbentSuppliers: string[]) {
  return items.map((item) => ({
    ...item,
    tagLabel: incumbentSuppliers.includes(item.id) ? "Incumbent" : undefined,
  }));
}

const Container = styled(Flex)({
  flexDirection: "column",
  overflow: "auto",
  padding: 8,
  gap: 16,
});

export default SupplierSelectPopover;
